/**
 * PerformanceChart Component
 * Displays rider performance history over time with multiple chart types
 * Supports Line, Bar, and Scatter chart visualizations
 * Allows filtering by bike (race number)
 * X-axis shows run type labels (P1, P2, T1, T2, Top10)
 */

import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

function PerformanceChart({ data, rider, hill }) {
  const [chartType, setChartType] = useState('line'); // 'line', 'bar', or 'scatter'
  const [selectedBikes, setSelectedBikes] = useState(new Set());

  const bikeKey = (run) =>
    `${run.race_number}|${run.manufacturer || ''}|${run.machine_type || ''}`;

  /**
   * Get unique bikes from the data (identified by race number + manufacturer + type)
   * Initialize all bikes as selected on first render
   */
  const getBikesList = () => {
    const bikesMap = new Map();
    (data || []).forEach(run => {
      const key = bikeKey(run);
      if (!bikesMap.has(key)) {
        bikesMap.set(key, {
          key,
          raceNumber: run.race_number,
          manufacturer: run.manufacturer,
          capacity: run.capacity,
          machineType: run.machine_type
        });
      }
    });
    return Array.from(bikesMap.values());
  };

  const bikes = getBikesList();

  // Initialize all bikes as selected on mount. Hooks must run unconditionally
  // on every render (not skipped by the "no data" early return below), so
  // this - and every other hook in this component - stays above that check;
  // getBikesList()/bikes above already tolerate empty/missing data safely.
  useEffect(() => {
    if (selectedBikes.size === 0) {
      setSelectedBikes(new Set(bikes.map(b => b.key)));
    }
  }, [bikes.length]);

  if (!data || data.length === 0) {
    return <div className="no-data">No performance data available</div>;
  }

  /**
   * Toggle bike selection
   */
  const toggleBike = (key) => {
    const newSelected = new Set(selectedBikes);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedBikes(newSelected);
  };

  /**
   * Select/deselect all bikes
   */
  const toggleAll = () => {
    if (selectedBikes.size === bikes.length) {
      setSelectedBikes(new Set());
    } else {
      setSelectedBikes(new Set(bikes.map(b => b.key)));
    }
  };

  /**
   * Filter data based on selected bikes
   */
  const getFilteredData = () => {
    return data.filter(run => selectedBikes.has(bikeKey(run)));
  };

  const filteredData = getFilteredData();

  /**
   * Transform data for chart display
   * Shows individual runs with run type labels for X-axis (P1, P2, T1, T2, Top10, etc.)
   */
  const transformData = () => {
    return filteredData.map((run, index) => {
      // Create short run type label
      let runLabel = '';
      if (run.run_type === 'Practice') {
        runLabel = `P${run.run_number || ''}`;
      } else if (run.run_type === 'Timed') {
        runLabel = `T${run.run_number || ''}`;
      } else if (run.run_type === 'TopTen') {
        runLabel = 'Top10';
      }
      
      return {
        index: index + 1,
        runLabel: runLabel,
        time: run.finish_time,
        date: new Date(run.date).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        event: run.event_name,
        bike: `#${run.race_number} - ${run.manufacturer} ${run.capacity}cc`,
        runType: run.run_type,
        // Color coding for scatter plot
        fill: run.run_type === 'Practice' ? '#3498db' : 
              run.run_type === 'Timed' ? '#2ecc71' : '#f39c12'
      };
    });
  };

  const chartData = transformData();

  /**
   * Calculate statistics
   */
  const getStats = () => {
    if (filteredData.length === 0) {
      return { best: 0, worst: 0, avg: 0, totalRuns: 0 };
    }
    
    const times = filteredData.map(d => d.finish_time);
    const best = Math.min(...times);
    const worst = Math.max(...times);
    const avg = times.reduce((a, b) => a + b, 0) / times.length;
    
    return { best, worst, avg, totalRuns: times.length };
  };

  const stats = getStats();
  
  // Show message if no bikes selected
  if (selectedBikes.size === 0 || filteredData.length === 0) {
    return (
      <div className="performance-chart">
        <div className="chart-header">
          <h2>Performance History</h2>
          <div className="chart-info">
            <span className="rider-name">{rider.fullname}</span>
            <span className="hill-name">at {hill}</span>
          </div>
        </div>

        {/* Bike Filter */}
        <div className="bike-filter">
          <h3>Filter by Bike (Race Number)</h3>
          <button className="toggle-all-button" onClick={toggleAll}>
            Select All
          </button>
          <div className="bike-checkboxes">
            {bikes.map(bike => (
              <label key={bike.key} className="bike-checkbox">
                <input
                  type="checkbox"
                  checked={selectedBikes.has(bike.key)}
                  onChange={() => toggleBike(bike.key)}
                />
                <span className="bike-info">
                  <strong>#{bike.raceNumber}</strong> - {bike.manufacturer} {bike.capacity}cc
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="no-data">
          {selectedBikes.size === 0 
            ? 'Please select at least one bike to view performance data'
            : 'No data available for selected bikes'
          }
        </div>
      </div>
    );
  }

  /**
   * Custom tooltip to show detailed information
   */
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip">
          <p className="tooltip-date"><strong>{data.runLabel}</strong> - {data.date}</p>
          <p><strong>Time:</strong> {data.time.toFixed(2)}s</p>
          <p><strong>Event:</strong> {data.event}</p>
          <p><strong>Bike:</strong> {data.bike}</p>
        </div>
      );
    }
    return null;
  };

  /**
   * Render Line Chart
   */
  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="index"
          tickFormatter={(value) => chartData[value - 1]?.runLabel || value}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis 
          label={{ value: 'Time (seconds)', angle: -90, position: 'insideLeft' }}
          domain={['auto', 'auto']}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="time" 
          stroke="#e74c3c" 
          name="Time"
          strokeWidth={2}
          dot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  /**
   * Render Bar Chart
   */
  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="index"
          tickFormatter={(value) => chartData[value - 1]?.runLabel || value}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis 
          label={{ value: 'Time (seconds)', angle: -90, position: 'insideLeft' }}
          domain={['auto', 'auto']}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar dataKey="time" fill="#3498db" name="Time" />
      </BarChart>
    </ResponsiveContainer>
  );

  /**
   * Render Scatter Chart - color coded by run type
   */
  const renderScatterChart = () => {
    // Group by run type for color coding
    const practiceData = chartData.filter(d => d.runType === 'Practice');
    const timedData = chartData.filter(d => d.runType === 'Timed');
    const toptenData = chartData.filter(d => d.runType === 'TopTen');

    return (
      <ResponsiveContainer width="100%" height={400}>
        <ScatterChart margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="index"
            type="number"
            label={{ value: 'Run Sequence', position: 'insideBottom', offset: -10 }}
            domain={['dataMin', 'dataMax']}
          />
          <YAxis 
            dataKey="time"
            label={{ value: 'Time (seconds)', angle: -90, position: 'insideLeft' }}
            domain={['auto', 'auto']}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {practiceData.length > 0 && (
            <Scatter 
              name="Practice" 
              data={practiceData} 
              fill="#3498db"
            />
          )}
          {timedData.length > 0 && (
            <Scatter 
              name="Timed" 
              data={timedData} 
              fill="#2ecc71"
            />
          )}
          {toptenData.length > 0 && (
            <Scatter 
              name="Top Ten" 
              data={toptenData} 
              fill="#f39c12"
            />
          )}
        </ScatterChart>
      </ResponsiveContainer>
    );
  };

  return (
    <div className="performance-chart">
      <div className="chart-header">
        <h2>Performance History</h2>
        <div className="chart-info">
          <span className="rider-name">{rider.fullname}</span>
          <span className="hill-name">at {hill}</span>
        </div>
      </div>

      {/* Chart Type Selector */}
      <div className="chart-controls">
        <div className="chart-type-buttons">
          <button
            className={chartType === 'line' ? 'active' : ''}
            onClick={() => setChartType('line')}
          >
            📈 Line Chart
          </button>
          <button
            className={chartType === 'bar' ? 'active' : ''}
            onClick={() => setChartType('bar')}
          >
            📊 Bar Chart
          </button>
          <button
            className={chartType === 'scatter' ? 'active' : ''}
            onClick={() => setChartType('scatter')}
          >
            🔵 Scatter Plot
          </button>
        </div>
      </div>

      {/* Bike Filter */}
      {bikes.length > 1 && (
        <div className="bike-filter">
          <h3>🏍️ Filter by Bike (Race Number)</h3>
          <button className="toggle-all-button" onClick={toggleAll}>
            {selectedBikes.size === bikes.length ? 'Deselect All' : 'Select All'}
          </button>
          <div className="bike-checkboxes">
            {bikes.map(bike => (
              <label key={bike.key} className="bike-checkbox">
                <input
                  type="checkbox"
                  checked={selectedBikes.has(bike.key)}
                  onChange={() => toggleBike(bike.key)}
                />
                <span className="bike-info">
                  <strong>#{bike.raceNumber}</strong> - {bike.manufacturer || 'Unknown'} {bike.capacity}cc ({bike.machineType})
                </span>
              </label>
            ))}
          </div>
          <p className="filter-hint">
            💡 Showing {filteredData.length} run{filteredData.length !== 1 ? 's' : ''} from {selectedBikes.size} bike{selectedBikes.size !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {/* Chart Display */}
      <div className="chart-container">
        {chartType === 'line' && renderLineChart()}
        {chartType === 'bar' && renderBarChart()}
        {chartType === 'scatter' && renderScatterChart()}
      </div>

      {/* Statistics Summary */}
      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-label">Total Runs:</span>
          <span className="stat-value">{stats.totalRuns}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Best Time:</span>
          <span className="stat-value best">{stats.best.toFixed(2)}s</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Average:</span>
          <span className="stat-value">{stats.avg.toFixed(2)}s</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Worst Time:</span>
          <span className="stat-value worst">{stats.worst.toFixed(2)}s</span>
        </div>
      </div>
    </div>
  );
}

export default PerformanceChart;
